import { toDateParam, tt } from '@agos/shared'

/**
 * The seven periods the dashboard filter offers, and the reason this file exists.
 *
 * **FB-171.** Legacy builds every range with `new Date(y, m, d)` — **local** calendar components — and then
 * formats it with `toISOString().split("T")[0]`, which converts to **UTC** first. Slovakia is UTC+1 in winter
 * and UTC+2 in summer, never behind, so a local midnight always renders as the previous day.
 *
 * For *Tento mesiac* and *Minulý mesiac* the whole range is built from `new Date(…, 1)`, so both are shifted
 * by a day **at every hour of the day, all year round**:
 *
 * | Preset | Asks for | Should ask for |
 * |---|---|---|
 * | Tento mesiac (July) | `2026-06-30 … 2026-07-29` | `2026-07-01 … 2026-07-29` |
 * | Minulý mesiac (June) | `2026-05-31 … 2026-06-29` | `2026-06-01 … 2026-06-30` |
 *
 * Measured against the restored data: *Minulý mesiac* reports **53 679,70 €** for a June that took
 * **54 559,55 €** — it drops 30 June and picks up 31 May instead. *Tento mesiac* reports **47 404,50 €**
 * against a real 44 807,65 €, because 30 June is in it. The first screen anybody opens, off by hundreds to
 * thousands of euros, every day.
 *
 * *Dnes*, *Včera* and the two week presets carry the current time of day, so they only cross the boundary
 * while local time is behind the offset — 00:00–02:00 in summer, 00:00–01:00 in winter. A pizzeria cashing
 * up at half past midnight is inside that window, and *Dnes* shows it yesterday.
 *
 * Legacy already had the fix. `lib/utils.ts` exports `toDateParam` with the comment *"`toISOString()` would
 * round-trip through UTC and a Bratislava-local midnight (UTC+1/+2) would render as the previous day, making
 * 'today' queries span two calendar days server-side"* — and `dashboard.tsx` declares its own `isoDate()`
 * three lines into the file and uses that instead. The helper here is `@agos/shared`'s `toDateParam`, which
 * is that same function, ported comment and all.
 *
 * The server cuts its days in `Europe/Bratislava` (`BusinessDay.ZONE`, named explicitly and never taken from
 * the JVM default), so a local calendar date is exactly what it is asking for.
 */

export type PeriodKey =
  | 'dnes'
  | 'vcera'
  | 'tyzdent'
  | 'minulytyzdent'
  | 'mesiac'
  | 'minulymesiac'
  | 'vlastny'

export interface DateRange {
  from: string
  to: string
}

/** The six quick pills, in legacy's order. `vlastny` is the date inputs, not a pill. */
export const QUICK_PERIODS: readonly PeriodKey[] = [
  'dnes',
  'vcera',
  'tyzdent',
  'minulytyzdent',
  'mesiac',
  'minulymesiac',
]

export const PERIOD_KEYS: readonly PeriodKey[] = [...QUICK_PERIODS, 'vlastny']

export function periodLabel(key: PeriodKey): string {
  if (key === 'dnes') return tt('Dnes')
  if (key === 'vcera') return tt('Včera')
  if (key === 'tyzdent') return tt('Tento týždeň')
  if (key === 'minulytyzdent') return tt('Minulý týždeň')
  if (key === 'mesiac') return tt('Tento mesiac')
  if (key === 'minulymesiac') return tt('Minulý mesiac')
  return tt('Vlastný rozsah')
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date)
  copy.setDate(copy.getDate() + days)
  return copy
}

/** Monday of the week `date` falls in. `getDay()` is 0 for Sunday, so Monday is `(day + 6) % 7` back. */
function mondayOf(date: Date): Date {
  return addDays(date, -((date.getDay() + 6) % 7))
}

/**
 * The range a preset means, as two local calendar dates.
 *
 * `today` is injectable so the tests can stand at half past midnight and at the first of the month without
 * waiting for either — the two moments where legacy's version parts company with the calendar.
 */
export function periodRange(key: PeriodKey, today: Date = new Date()): DateRange {
  const day = toDateParam(today)

  if (key === 'vcera') {
    const yesterday = toDateParam(addDays(today, -1))
    return { from: yesterday, to: yesterday }
  }
  if (key === 'tyzdent') {
    return { from: toDateParam(mondayOf(today)), to: day }
  }
  if (key === 'minulytyzdent') {
    const monday = addDays(mondayOf(today), -7)
    return { from: toDateParam(monday), to: toDateParam(addDays(monday, 6)) }
  }
  if (key === 'mesiac') {
    return { from: toDateParam(new Date(today.getFullYear(), today.getMonth(), 1)), to: day }
  }
  if (key === 'minulymesiac') {
    const first = new Date(today.getFullYear(), today.getMonth() - 1, 1)
    // Day 0 of this month is the last day of the previous one, whatever its length.
    const last = new Date(today.getFullYear(), today.getMonth(), 0)
    return { from: toDateParam(first), to: toDateParam(last) }
  }
  // `dnes`, and the fallback legacy uses for `vlastny` before either input is touched.
  return { from: day, to: day }
}

/** `2026-07-27` → `27.07.2026`, the format the filter bar shows. */
export function formatRangeDate(iso: string): string {
  const [year, month, day] = iso.split('-')
  if (year === undefined || month === undefined || day === undefined) return iso
  return `${day}.${month}.${year}`
}

export function rangeLabel(key: PeriodKey, range: DateRange): string {
  if (key !== 'vlastny') return periodLabel(key)
  if (range.from === range.to) return formatRangeDate(range.from)
  return `${formatRangeDate(range.from)} - ${formatRangeDate(range.to)}`
}

export function isMultiDay(range: DateRange): boolean {
  return range.from !== range.to
}

/**
 * Whether the range runs backwards.
 *
 * Two date inputs and nothing stopping *from* being after *to*. The server answers such a range with zeroes
 * rather than an error, so legacy draws an empty dashboard that looks exactly like a day with no trade.
 */
export function rangeIsBackwards(range: DateRange): boolean {
  return range.from > range.to
}

/** How many days the range covers, inclusive. Used to say what a total is a total *of*. */
export function rangeDays(range: DateRange): number {
  const from = Date.parse(`${range.from}T00:00:00Z`)
  const to = Date.parse(`${range.to}T00:00:00Z`)
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return 0
  return Math.round((to - from) / 86_400_000) + 1
}
